import React, { useState } from 'react'
import { Subscription } from '../../../interfaces/subscription'
import { useTranslation } from 'react-i18next'
import Button from '../../Buttons/Button'
import ModalCancelSubscription from '../../Modals/ModalCancelSubscription'
import ChangePlanModal from '../../Modals/ChangePlanModal'
import useCancelChangePlan from '../../../services/subscription/useCancelChangePlan'
import Spinner from '../../UI/Spinner'
import { PricingPlan } from '../../../interfaces/pricingPlan'

interface Props {
  activeSubscription: Subscription
  pricingPlans: PricingPlan[]
}

const PlanActions = ({ activeSubscription, pricingPlans = [] }: Props) => {
  const { t } = useTranslation()
  const [isOpenCancelSubscription, setIsOpenCancelSubscription] =
    useState<boolean>(false)
  const [isOpenChangePlan, setIsOpenChangePlan] = useState<boolean>(false)
  const changePlanModalHandler = () => setIsOpenChangePlan((prev) => !prev)
  const cancelSubscriptionModalHandler = () =>
    setIsOpenCancelSubscription((prev) => !prev)
  const { mutate: cancelChangePlan, isLoading: isCancellingChangePlan } =
    useCancelChangePlan()

  const isSubscriptionCancelled = activeSubscription?.cancel_at_period_end
  const hasPendingChangePlan = activeSubscription?.pending_plan_change
  // `cancel` and `change-plan` both refuse a bank transfer subscription with
  // a 400 - they drive Stripe, and these are settled off it. Offering the
  // buttons anyway only produced a failed request the customer could not act
  // on, so say who to talk to instead.
  const isManagedBySupport = activeSubscription?.provider === 'bank_transfer'

  const onChangePlanHandler = () => {
    if (hasPendingChangePlan) {
      cancelChangePlan({ subscriptionId: activeSubscription.id })
      return
    }

    changePlanModalHandler()
  }

  return (
    <>
      {isOpenChangePlan && (
        <ChangePlanModal
          onClose={changePlanModalHandler}
          activeSubscription={activeSubscription}
          pricingPlans={pricingPlans}
        />
      )}
      {isOpenCancelSubscription && (
        <ModalCancelSubscription
          onClose={cancelSubscriptionModalHandler}
          subscriptionId={activeSubscription.id}
          isSubscriptionCancelled={isSubscriptionCancelled}
        />
      )}

      {isManagedBySupport ? (
        <p className='text-lightGray'>{t('subscriptionManagedBySupport')}</p>
      ) : (
        <div className='flex gap-5 flex-col-reverse sm:gap-5 sm:justify-end sm:flex-row'>
          {!isSubscriptionCancelled && (
            <button
              type='button'
              className='text-deleteRed'
              onClick={cancelSubscriptionModalHandler}
            >
              {t('cancelSubscription')}
            </button>
          )}
          {isSubscriptionCancelled && (
            <Button
              width='min-w-full sm:min-w-[180px]'
              type='button'
              text={t('reActivateSubscription')}
              onClickHandler={cancelSubscriptionModalHandler}
            />
          )}
          {!isSubscriptionCancelled && (
            <Button
              bgColor='bg-[#E1ECFC]'
              textColor='text-black'
              width='min-w-full sm:min-w-[180px]'
              type='button'
              disabled={activeSubscription?.cancel_at_period_end}
              text={t(hasPendingChangePlan ? 'cancelPlanUpdate' : 'changePlan')}
              onClickHandler={onChangePlanHandler}
            >
              {isCancellingChangePlan && (
                <Spinner spinner='form' color='#0C75FF' />
              )}
            </Button>
          )}
        </div>
      )}
    </>
  )
}

export default PlanActions
